import i18n from 'helpers/i18n';
import messages from './messages';
import debounce from 'debounce';
import { memo, useMemo, useState } from 'react';
import { Text, Label } from '@teachstonellc/component-library';
import { AgeLevel } from 'types/api/envScales/AgeLevel';
import { SectionCollapse } from 'components/SectionCollapse';
import { abbrToUppercase } from 'helpers/dimension';
import { BulletedTextArea } from './BulletedTextArea';
import { updateObservationCycleScores } from 'actions/envScales/observationCycleScores';

interface CycleNotesFormProp {
  /** Age Level. */
  ageLevel: AgeLevel;

  /** Observation Cycle ID. */
  cycleId: number;

  /** Observation Cycle number. */
  cycleNumber: number;

  /** Notes by dimension ID. */
  notesByDimensionId: Record<number, string>;

  /** Callback fired when notes change. */
  onNotesByDimensionIdChange: (dimensionId: number, notes: string) => void;
}

const DEBOUNCE_DELAY = 300;

function CycleNotesForm({
  ageLevel,
  cycleId,
  notesByDimensionId,
  onNotesByDimensionIdChange
}: CycleNotesFormProp) {
  const dimensions = useMemo(
    () => ageLevel.domains.flatMap(domain => domain.dimensions),
    [ageLevel.domains]
  );

  // Observation notes have no required fields, so no error/completed state
  // is ever meaningful here -- always show the "Optional" tag.
  const optionalStatus = (
    <Label className="ml-auto" color="yellow">
      {i18n.ft(messages.optional)}
    </Label>
  );

  return (
    <SectionCollapse
      className="mt-2"
      title={i18n.ft(messages.title)}
      subtitle={i18n.ft(messages.sectionDescription)}
      closeOthersOnOpen
      status={optionalStatus}
    >
      <div className="flex flex-col gap-6">
        {dimensions.map(dimension => (
          <DimensionNotesInputMemoized
            key={dimension.id}
            notes={notesByDimensionId[dimension.id] ?? ''}
            label={`${dimension.name} (${abbrToUppercase(dimension.abbreviation)})`}
            cycleId={cycleId}
            dimensionId={dimension.id}
            onNotesByDimensionIdChange={onNotesByDimensionIdChange}
          />
        ))}
      </div>
    </SectionCollapse>
  );
}

interface DimensionNotesInputProps {
  /** Label for the notes input. */
  label: string;

  /** Initial notes for the dimension. */
  notes: string;

  /** Observation Cycle ID. */
  cycleId: number;

  /** Dimension ID. */
  dimensionId: number;

  /** Callback fired when notes change. */
  onNotesByDimensionIdChange: (dimensionId: number, notes: string) => void;
}

const DimensionNotesInputMemoized = memo(function DimensionNotesInput({
  label,
  notes,
  cycleId,
  dimensionId,
  onNotesByDimensionIdChange
}: DimensionNotesInputProps) {
  const [value, setValue] = useState(notes);

  function handleChange(newValue: string) {
    setValue(newValue);
    debouncedSaveNotes(newValue);
    onNotesByDimensionIdChange(dimensionId, newValue);
  }

  const debouncedSaveNotes = useMemo(
    () =>
      debounce((notes: string) => {
        updateObservationCycleScores(cycleId, {
          observation_cycle_score: {
            dimension_id: dimensionId,
            dimension_notes: notes
          }
        });
      }, DEBOUNCE_DELAY),
    [cycleId]
  );

  return (
    <BulletedTextArea
      value={value}
      label={label}
      placeholder={i18n.ft(messages.notesPlaceholder)}
      onChange={value => handleChange(value)}
    />
  );
});

export default CycleNotesForm;
